import { useEffect, useRef } from "react";
import type { DeviceMode } from "@/mocks/deviceModes";

interface ModeSelectorProps {
  modes: DeviceMode[];
  activeId: string;
  onSelect: (id: string) => void;
}

export default function ModeSelector({ modes, activeId, onSelect }: ModeSelectorProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  useEffect(() => {
    const container = containerRef.current;
    const el = itemRefs.current[activeId];
    if (!container || !el) return;
    const target = el.offsetLeft - (container.clientWidth - el.clientWidth) / 2;
    container.scrollTo({ left: target, behavior: "smooth" });
  }, [activeId]);

  return (
    <div className="mt-3">
      <div className="mb-1.5 flex items-center justify-between">
        <p className="text-sm font-medium text-foreground-800">モード</p>
        <span className="flex items-center gap-1 text-[11px] text-foreground-400">
          <i className="ri-drag-move-2-line" /> 横にスワイプ
        </span>
      </div>
      <div
        ref={containerRef}
        className="no-scrollbar -mx-6 flex snap-x snap-mandatory gap-2 overflow-x-auto px-6 pb-1"
      >
        {modes.map((m) => {
          const active = m.id === activeId;
          return (
            <button
              key={m.id}
              type="button"
              ref={(node) => {
                itemRefs.current[m.id] = node;
              }}
              onClick={() => onSelect(m.id)}
              className={`flex h-[76px] w-[136px] shrink-0 snap-start cursor-pointer flex-col items-start gap-1 rounded-lg border px-3 py-2 text-left transition-all duration-200 ${
                active
                  ? "border-primary-300 bg-primary-500 text-background-50"
                  : "border-background-200 bg-background-100/60 text-foreground-700 hover:border-primary-200"
              }`}
            >
              <span className="flex items-center gap-1.5 whitespace-nowrap text-sm font-semibold">
                <i className={`${m.icon} text-base`} />
                {m.name}
              </span>
              <span
                className={`line-clamp-2 text-[11px] leading-tight ${
                  active ? "text-background-50/80" : "text-foreground-400"
                }`}
              >
                {m.desc}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}